import { Box, Stack } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { Sidebar } from './Sidebar';
import { TopNavigation } from './TopNavigation';

import { MAIN_CONTENT_ID, SkipLinks } from '@/components/SkipLinks';
import { isBoolean, usePersistedState } from '@/lib';

/**
 * The app shell, matching the Project Portfolio framing in
 * Web/src/components/layouts/DefaultLayout/.
 *
 * The geometry is worth stating because it looks arbitrary otherwise: the grey
 * body shows through on all four sides of the white page card, but only two of
 * those gaps are padding. The top gap is the 48px `TopNavigation` sitting on the
 * grey; the left gap is the sidebar's own `padding-left: 20px`. Only the right
 * and bottom are real padding, hence `0 10px 10px 0`.
 *
 * When the sidebar is hidden the card takes the full width and gains its own
 * 10px left margin, since the gutter the sidebar was providing disappears.
 *
 * Focus moves to `<main>` on every route change, as Web's DefaultLayout does —
 * without it a keyboard or screen-reader user stays parked on the link they
 * activated while the whole page changes underneath them.
 */
export type AppShellProps = {
  /**
   * `contained` centres the route in the card for reading-width pages;
   * `full-bleed` lets the route own the whole card, for the panelled pages.
   */
  variant?: 'contained' | 'full-bleed';
};

export const AppShell = ({ variant = 'contained' }: AppShellProps) => {
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedState(
    'pm.sidebar.collapsed',
    false,
    isBoolean,
  );

  useEffect(() => {
    mainRef.current?.focus();
  }, [pathname]);

  return (
    <Box sx={{ height: '100dvh', bgcolor: 'var(--bg-app)', overflow: 'hidden' }}>
      <SkipLinks />
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ flexGrow: 1, overflow: 'hidden', pr: '10px', pb: '10px' }}>
          <TopNavigation
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          <Stack direction="row" gap={0}>
            <Sidebar collapsed={sidebarCollapsed} />
            <Box
              component="main"
              id={MAIN_CONTENT_ID}
              ref={mainRef}
              // -1 so the skip link and the route-change effect can focus it
              // without adding the whole page body to the tab order.
              tabIndex={-1}
              data-automation-id="page-wrapper"
              sx={{
                position: 'relative',
                width: '100%',
                height: 'var(--page-wrapper-height)',
                maxWidth: sidebarCollapsed ? '100%' : 'var(--page-wrapper-max-width)',
                ...(sidebarCollapsed ? { ml: '10px' } : {}),
                bgcolor: 'var(--color-black-00)',
                borderRadius: 'var(--border-radius-medium)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: variant === 'contained' ? 'auto' : 'hidden',
                '&:focus': { outline: 'none' },
              }}
            >
              {variant === 'contained' ? (
                <Box sx={{ p: 3, maxWidth: 'var(--content-max-width)', width: '100%', mx: 'auto' }}>
                  <Outlet />
                </Box>
              ) : (
                <Outlet />
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
